import { createRoot } from 'react-dom/client';
import { createApiClient } from './api/client';
import { readRuntimeConfig, type ConditionalRule } from './config';
import { createStripeBridge } from './payments/stripe';
import { createFormController } from './state/controller';
import { playSitePreloader } from './integrations/preloader';
import { completeBooking } from './tracking/complete';
import { purchaseFromBooking, trackPurchase } from './tracking/ga4';
import { navigateTo, thankYouUrl } from './tracking/redirect';
import { bookingMeta, captureUtm } from './tracking/utm';
import { App } from './ui/App';
import { FormContext } from './ui/context';
import './ui/styles.css';

async function boot(): Promise<void> {
  const mount = document.querySelector<HTMLElement>('[data-cbf-root]');
  if (!mount) return;
  const config = readRuntimeConfig();
  const utm = captureUtm();

  let fetchImpl: typeof fetch | undefined;
  let rules: ConditionalRule[] = config.conditionals;
  let stripeFactory = createStripeBridge;
  if (import.meta.env.DEV && config.mock) {
    // Dev-server only; the whole branch is tree-shaken from production builds. The Stripe
    // bridge is mocked too — real Stripe.js rejects the mock API's fake intent secrets.
    const [{ createMockTransport }, { PRODUCTION_POC_RULE }, { createMockStripeBridge }] =
      await Promise.all([
        import('./api/mock/transport'),
        import('./api/mock/overlay'),
        import('./payments/mockBridge'),
      ]);
    fetchImpl = createMockTransport({ baseUrl: config.apiBase, latencyMs: 150 }).fetch;
    rules = [...rules, PRODUCTION_POC_RULE];
    stripeFactory = createMockStripeBridge;
  }

  try {
    const client = createApiClient({
      baseUrl: config.apiBase,
      env: config.env,
      allowProduction: config.allowProduction,
      fetch: fetchImpl,
    });
    const stripe = stripeFactory();
    const controller = createFormController({
      client,
      config,
      rules,
      confirmCardSetup: (secret, billing) => stripe.confirmCardSetup(secret, billing),
      meta: () => bookingMeta(utm),
      onSuccess: (result, state) => {
        const currency = (state.bootstrap?.settings.currency?.code ?? 'usd').toUpperCase();
        const purchase = purchaseFromBooking(result, currency);
        // 'summary' keeps the customer on the page: the confirmation panel with the order summary.
        const url =
          config.afterBooking === 'redirect' ? thankYouUrl(state.bootstrap?.settings) : null;
        void completeBooking({
          track: () => (purchase ? trackPurchase(purchase) : Promise.resolve('none' as const)),
          animate: () => playSitePreloader(),
          redirect: url ? () => navigateTo(url) : null,
        });
      },
    });
    mount.removeAttribute('aria-busy');
    createRoot(mount).render(
      <FormContext.Provider value={{ controller, config, stripe, now: () => new Date() }}>
        <App />
      </FormContext.Provider>,
    );
    const state = await controller.start();
    if (
      state.values.paymentMethod === 'stripe' ||
      (state.bootstrap?.settings.payment_methods?.stripe ?? false)
    ) {
      void stripe.load(state.bootstrap?.settings.stripe_public_key);
    }
  } catch (err) {
    mount.textContent =
      'The booking form could not be started. Please refresh the page or contact us to book.';
    console.error('[cbf]', err);
  }
}

if (document.readyState === 'loading')
  document.addEventListener('DOMContentLoaded', () => void boot());
else void boot();
